/**
 * Shared fleet-locations telemetry store — Fleet Map UX Hardening.
 *
 * Singleton, subscribe/emit. Any number of surfaces (Dashboard preview,
 * Fleet Map workspace, future Ops workspace) can call
 *   const { rows, fetchedAt, refresh, isRefreshing, refreshError } = useFleetLocations()
 * and share ONE fetch cycle. Only one HTTP request in flight at any time.
 *
 * Refresh policy (per brief):
 *   - Fixed 15-minute interval (FLEET_LOCATION_REFRESH_INTERVAL_MS)
 *   - Auto-refresh only while at least one consumer is mounted AND the
 *     document is visible. Hidden tab → pause. Tab regain → if cached
 *     data older than the interval, refresh immediately, else resume
 *     normal schedule.
 *   - Manual refresh available; 15s cooldown prevents spam.
 *   - Refresh failures: KEEP last valid rows in place, expose
 *     `refreshError` so the UI can display a banner + Retry. Never
 *     erase markers or invent coordinates.
 *   - Automatic refresh MUST NOT reload the Google Maps SDK, the page,
 *     or the Map instance itself — this store hands new rows to
 *     already-mounted consumers, which mutate marker positions in place.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Central refresh interval — the ONE place this is defined. Do not
// duplicate. Wave 2D.1+ notification evaluators may cross-reference
// this constant when deciding staleness thresholds.
export const FLEET_LOCATION_REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

// Cooldown between successful manual refreshes.
export const MANUAL_REFRESH_COOLDOWN_MS = 15 * 1000; // 15 seconds

// --- module-level singleton state ---
const state = {
  rows: [],
  fetchedAt: null,        // ISO string — application fetch time
  isRefreshing: false,
  refreshError: null,     // last failure message; cleared on success
  lastManualAt: 0,        // ms epoch of last manual refresh completion
  timerId: null,
  cooldownTimerId: null,  // fires one emit at MANUAL cooldown boundary
  inflight: null,         // Promise if a fetch is currently in flight
  subscribers: new Set(), // set of setState functions
};

const emit = () => {
  const snapshot = {
    rows: state.rows,
    fetchedAt: state.fetchedAt,
    isRefreshing: state.isRefreshing,
    refreshError: state.refreshError,
  };
  state.subscribers.forEach((s) => s(snapshot));
};

async function doFetch({ manual }) {
  if (state.inflight) return state.inflight;
  state.isRefreshing = true;
  emit();
  const p = api
    .get("/fleet/locations")
    .then((r) => {
      const data = r?.data || {};
      state.rows = Array.isArray(data.rows) ? data.rows : [];
      state.fetchedAt = data.fetched_at || new Date().toISOString();
      state.refreshError = null;
      if (manual) state.lastManualAt = Date.now();
    })
    .catch((e) => {
      // Preserve last valid rows + timestamp; surface error text.
      state.refreshError =
        e?.response?.data?.detail ||
        e?.message ||
        "Location refresh failed — showing last known positions";
    })
    .finally(() => {
      state.isRefreshing = false;
      state.inflight = null;
      emit();
      // Fleet Map UX Hardening — the manual-refresh cooldown is a
      // pure elapsed-time gate (see `canManualRefresh` below). React
      // will not re-render just because time passed, so without an
      // explicit timer the button would remain disabled until some
      // *other* state change (e.g. the next 15-min auto-refresh)
      // caused an emit. Fire one emit at the cooldown boundary so the
      // button re-enables promptly.
      if (manual && state.lastManualAt) {
        if (state.cooldownTimerId) clearTimeout(state.cooldownTimerId);
        state.cooldownTimerId = setTimeout(() => {
          state.cooldownTimerId = null;
          emit();
        }, MANUAL_REFRESH_COOLDOWN_MS + 50);
      }
    });
  state.inflight = p;
  return p;
}

function scheduleNext() {
  if (state.timerId) clearTimeout(state.timerId);
  if (state.subscribers.size === 0) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const intervalMs = __getEffectiveIntervalMs();
  state.timerId = setTimeout(() => {
    // Only refresh if still relevant and visible
    if (
      state.subscribers.size > 0 &&
      (typeof document === "undefined" || document.visibilityState !== "hidden")
    ) {
      doFetch({ manual: false }).then(scheduleNext);
    }
  }, intervalMs);
}

function onVisibilityChange() {
  if (state.subscribers.size === 0) return;
  if (document.visibilityState === "hidden") {
    if (state.timerId) {
      clearTimeout(state.timerId);
      state.timerId = null;
    }
    return;
  }
  // Tab returned. If cache is missing or older than the refresh
  // interval, refresh immediately; either way, reschedule.
  const stale =
    !state.fetchedAt ||
    Date.now() - new Date(state.fetchedAt).getTime() >
      __getEffectiveIntervalMs();
  if (stale) {
    doFetch({ manual: false }).then(scheduleNext);
  } else {
    scheduleNext();
  }
}

let visibilityWired = false;
function ensureVisibilityWired() {
  if (visibilityWired || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  visibilityWired = true;
}

// Test-only knob — allows the tester to temporarily shorten the
// interval without touching the constant. Prod code MUST NOT use this.
// Set via `window.__AA_FLEET_REFRESH_MS = 5000` before the surface
// mounts, or via the exported `__setTestRefreshIntervalMs`. When null/
// unset, the exported `FLEET_LOCATION_REFRESH_INTERVAL_MS` is used.
let _testIntervalOverrideMs = null;
export function __setTestRefreshIntervalMs(ms) {
  _testIntervalOverrideMs = ms;
}
export function __getEffectiveIntervalMs() {
  if (typeof window !== "undefined" && typeof window.__AA_FLEET_REFRESH_MS === "number") {
    return window.__AA_FLEET_REFRESH_MS;
  }
  return _testIntervalOverrideMs ?? FLEET_LOCATION_REFRESH_INTERVAL_MS;
}

/**
 * React hook. Returns a snapshot of the shared store plus an imperative
 * `refresh({ force })` — respects the manual cooldown unless `force`.
 * `canManualRefresh` is true when the cooldown has elapsed and no
 * refresh is currently in flight.
 */
export function useFleetLocations() {
  const [snap, setSnap] = useState({
    rows: state.rows,
    fetchedAt: state.fetchedAt,
    isRefreshing: state.isRefreshing,
    refreshError: state.refreshError,
  });

  useEffect(() => {
    ensureVisibilityWired();
    state.subscribers.add(setSnap);
    // Initial fetch if we have nothing cached yet or cache is stale.
    if (state.subscribers.size === 1) {
      const stale =
        !state.fetchedAt ||
        Date.now() - new Date(state.fetchedAt).getTime() >
          __getEffectiveIntervalMs();
      if (stale) {
        doFetch({ manual: false }).then(scheduleNext);
      } else {
        scheduleNext();
      }
    }
    return () => {
      state.subscribers.delete(setSnap);
      if (state.subscribers.size === 0) {
        if (state.timerId) clearTimeout(state.timerId);
        state.timerId = null;
      }
    };
  }, []);

  const cooldownRemaining = Math.max(
    0,
    MANUAL_REFRESH_COOLDOWN_MS - (Date.now() - state.lastManualAt)
  );
  const canManualRefresh =
    !state.isRefreshing && cooldownRemaining === 0;

  const refresh = async ({ force = false } = {}) => {
    if (state.isRefreshing) return;
    if (!force && cooldownRemaining > 0) return;
    await doFetch({ manual: true });
    // Reschedule from now so the 15-min timer resets after manual pull.
    scheduleNext();
  };

  return { ...snap, refresh, canManualRefresh };
}

// Test-only knob — allows the tester to temporarily shorten the
// interval without touching the constant. Prod code MUST NOT use this.
// The tester can set `window.__AA_FLEET_REFRESH_MS = 5000` before mount
// (see the definition of `__getEffectiveIntervalMs` above).
